'use client'

import { motion } from 'framer-motion'
import { Building2 } from 'lucide-react'

export default function Industries() {
  const industries = [
    { icon: '🏥', title: 'Healthcare', projects: '50+', color: 'from-sky-400 to-blue-500' },
    { icon: '🏦', title: 'Finance & Banking', projects: '40+', color: 'from-emerald-400 to-teal-500' },
    { icon: '🛒', title: 'E-commerce', projects: '80+', color: 'from-violet-400 to-purple-500' },
    { icon: '🎓', title: 'Education', projects: '35+', color: 'from-orange-400 to-amber-500' },
    { icon: '🏭', title: 'Manufacturing', projects: '25+', color: 'from-red-400 to-rose-500' },
    { icon: '🏠', title: 'Real Estate', projects: '30+', color: 'from-teal-400 to-cyan-500' },
    { icon: '🚗', title: 'Transportation', projects: '20+', color: 'from-indigo-400 to-blue-500' },
    { icon: '🎯', title: 'Marketing & Media', projects: '45+', color: 'from-pink-400 to-rose-500' },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
  }

  return (
    <section className="py-20 sm:py-28 bg-sky-50/30 relative overflow-hidden">
      <div className="absolute top-10 left-0 w-72 h-72 bg-sky-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-0 w-60 h-60 bg-blue-100/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4"
          >
            <Building2 className="w-4 h-4" />
            Industry Expertise
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Industries <span className="gradient-text">We Serve</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Custom software that drives efficiency across diverse industries
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-5"
        >
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -6, scale: 1.02 }}
              className="group bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50 hover:shadow-lg hover:shadow-sky-100/50 hover:border-sky-200 transition-all duration-300 text-center"
            >
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">{industry.icon}</div>
              <h3 className="font-semibold text-slate-900 text-sm mb-1 group-hover:text-sky-600 transition-colors">{industry.title}</h3>
              <span className={`text-xs font-medium bg-gradient-to-r ${industry.color} bg-clip-text text-transparent`}>
                {industry.projects} Projects
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-14"
        >
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(56,189,248,0.08),transparent)] pointer-events-none" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center relative z-10">
              {[
                { value: '325+', label: 'Total Projects', color: 'text-sky-400' },
                { value: '8+', label: 'Industries', color: 'text-emerald-400' },
                { value: '95%', label: 'Client Retention', color: 'text-violet-400' },
                { value: '24/7', label: 'Support', color: 'text-orange-400' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className={`text-3xl font-bold mb-1 ${stat.color}`}>{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
